import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, Trash2 } from 'lucide-react';
import type { Nodo } from '@doc/shared';
import { api } from '../../lib/api.js';
import { fecha, iconoDe, tamano } from '../../lib/formato.js';
import { Cabecera } from '../../layout/Marco.js';
import { Confirmar } from '../../ui/Modal.js';
import { useAvisos } from '../../ui/avisos.js';
import { claves, useAcciones } from '../explorador/consultas.js';

type EnPapelera = Nodo & { eliminadoPorNombre: string | null; ruta: string };

export function PapeleraPage() {
  const { data, isLoading } = useQuery({
    queryKey: claves.papelera,
    queryFn: () => api<EnPapelera[]>('/papelera'),
  });
  const { restaurar, purgar } = useAcciones();
  const { avisar, avisarError } = useAvisos();
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);

  const elementos = data ?? [];
  const seleccionados = elementos.filter((e) => seleccion.has(e.id));
  const todosMarcados = elementos.length > 0 && seleccionados.length === elementos.length;

  const alternarTodos = () =>
    setSeleccion(todosMarcados ? new Set() : new Set(elementos.map((e) => e.id)));

  const alternar = (id: string) =>
    setSeleccion((previa) => {
      const siguiente = new Set(previa);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });

  async function ejecutar(accion: 'restaurar' | 'purgar') {
    const ids = seleccionados.map((e) => e.id);
    if (ids.length === 0) return;
    try {
      if (accion === 'restaurar') {
        await restaurar.mutateAsync(ids);
        avisar(ids.length === 1 ? 'Elemento restaurado' : `${ids.length} elementos restaurados`);
      } else {
        await purgar.mutateAsync(ids);
        avisar('Eliminado definitivamente');
      }
      setSeleccion(new Set());
    } catch (error) {
      avisarError(error);
    }
  }

  return (
    <>
      <Cabecera
        titulo="Papelera"
        subtitulo="Lo eliminado se conserva hasta que alguien lo vacíe"
        acciones={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {seleccionados.length > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>
                {seleccionados.length} de {elementos.length} seleccionado(s)
              </span>
            )}
            <button className="btn" disabled={seleccionados.length === 0} onClick={() => void ejecutar('restaurar')}>
              <RotateCcw size={14} /> Restaurar
            </button>
            <button className="btn btn--peligro" disabled={seleccionados.length === 0} onClick={() => setConfirmando(true)}>
              <Trash2 size={14} /> Eliminar definitivamente
            </button>
          </div>
        }
      />

      <div className="pagina">
        {isLoading && <div className="cargando">Cargando…</div>}

        {!isLoading && elementos.length === 0 && (
          <div className="vacio">
            <div className="icono">
              <Trash2 size={36} />
            </div>
            <p>La papelera está vacía.</p>
          </div>
        )}

        {elementos.length > 0 && (
          <table className="tabla">
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <CasillaTodos
                    marcada={todosMarcados}
                    parcial={seleccionados.length > 0 && !todosMarcados}
                    alCambiar={alternarTodos}
                  />
                </th>
                <th>Nombre</th>
                <th>Ubicación original</th>
                <th style={{ width: 90 }}>Tamaño</th>
                <th style={{ width: 150 }}>Eliminado por</th>
                <th style={{ width: 170 }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {elementos.map((e) => {
                const { Icono, clase } = iconoDe(e);
                return (
                  <tr key={e.id} onClick={() => alternar(e.id)} style={{ cursor: 'pointer' }}>
                    <td>
                      <input
                        className="check"
                        type="checkbox"
                        checked={seleccion.has(e.id)}
                        onChange={() => alternar(e.id)}
                        onClick={(evento) => evento.stopPropagation()}
                        aria-label={`Seleccionar ${e.nombre}`}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <span className={`icono-tipo ${clase}`}>
                          <Icono size={15} />
                        </span>
                        {e.nombre}
                      </div>
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {e.ruta.slice(0, e.ruta.lastIndexOf('/')) || '/'}
                    </td>
                    <td className="muted">{e.tipo === 'CARPETA' ? '—' : tamano(e.bytes)}</td>
                    <td className="muted">{e.eliminadoPorNombre ?? '—'}</td>
                    <td className="muted">{fecha(e.eliminadoEn)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {confirmando && (
        <Confirmar
          titulo="Eliminar definitivamente"
          peligro
          etiquetaConfirmar="Eliminar para siempre"
          mensaje={
            <>
              Se borrarán <strong>{seleccionados.length} elemento(s)</strong> y sus versiones. Esta acción no se puede
              deshacer: el contenido deja de existir, no se puede recuperar desde la papelera.
            </>
          }
          alConfirmar={() => void ejecutar('purgar')}
          alCerrar={() => setConfirmando(false)}
        />
      )}
    </>
  );
}

/**
 * Casilla de "seleccionar todo".
 *
 * El estado intermedio —hay algo marcado, pero no todo— solo se puede fijar
 * desde JavaScript: no existe como atributo HTML. Sin él, la casilla se ve
 * vacía con media papelera seleccionada y engaña.
 */
function CasillaTodos({ marcada, parcial, alCambiar }: { marcada: boolean; parcial: boolean; alCambiar(): void }) {
  const casilla = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (casilla.current) casilla.current.indeterminate = parcial;
  }, [parcial]);

  return (
    <input
      ref={casilla}
      className="check"
      type="checkbox"
      checked={marcada}
      onChange={alCambiar}
      title={marcada ? 'Quitar la selección' : 'Seleccionar todo'}
      aria-label={marcada ? 'Quitar la selección' : 'Seleccionar todo'}
    />
  );
}
